import { jsx as _jsx } from "react/jsx-runtime";
import isUndefined from "lodash/isUndefined";
import { memo, useCallback, useEffect, useRef } from "react";
import { DrawingCanvas } from "~/components/BoundingBox/components/DrawingCanvas";
import { BoundingBoxCanvas } from "~/components/BoundingBox/utils/BoundingBoxCanvas";
import { BoundingBoxDrawer } from "~/components/BoundingBox/utils/BoundingBoxDrawer";
import { BoundingBoxMover } from "~/components/BoundingBox/utils/BoundingBoxMover";
import { BoundingBoxResizer } from "~/components/BoundingBox/utils/BoundingBoxResizer";
import { isMouseWithinMovableCorner } from "~/components/BoundingBox/utils/drawSquareAroundPoint";
import { findHoveredBox } from "~/components/BoundingBox/utils/findHoveredBox";
import { isPointInDrawRect } from "~/components/BoundingBox/utils/isPointInRect";
function _BoundingBoxLayer(param) {
    let { boundingBoxes, disabled, onIsSelectingChange, containerWidth, containerHeight, onBoundingBoxDrawn, onClear, shouldDrawOverlay, containerTop, containerLeft, hasCustomCursor, editableBoundingBoxIndex, drawNewBox, overlayColor, onBoxHovered, onMouseUp: _onMouseUp, onMouseDown: _onMouseDown, drawBoxes } = param;
    const drawingCanvasRef = useRef(null);
    const boundingBoxCanvas = useRef(new BoundingBoxCanvas({
        containerWidth,
        containerHeight,
        getDrawingCanvasHandle: ()=>drawingCanvasRef.current,
        overlayColor
    }));
    const boundingBoxHandler = useRef(null);
    useEffect(()=>{
        if (boundingBoxCanvas.current) {
            boundingBoxCanvas.current.containerWidth = containerWidth;
            boundingBoxCanvas.current.containerHeight = containerHeight;
        }
    }, [
        containerHeight,
        containerWidth
    ]);
    const onIsSelectingChangeRef = useRef(onIsSelectingChange);
    onIsSelectingChangeRef.current = onIsSelectingChange;
    const onMouseDownRef = useRef(_onMouseDown);
    onMouseDownRef.current = _onMouseDown;
    const onMouseUpRef = useRef(_onMouseUp);
    onMouseUpRef.current = _onMouseUp;
    const onBoundingBoxDrawnRef = useRef(onBoundingBoxDrawn);
    onBoundingBoxDrawnRef.current = onBoundingBoxDrawn;
    const hoveredCornerRef = useRef();
    const isHoveringBoxRef = useRef(false);
    const finishBoxDrawOnMouseUp = useCallback((rect)=>{
        var _boundingBoxHandler_current, _onIsSelectingChangeRef_current;
        if (rect) {
            var _onBoundingBoxDrawnRef_current;
            (_onBoundingBoxDrawnRef_current = onBoundingBoxDrawnRef.current) === null || _onBoundingBoxDrawnRef_current === void 0 ? void 0 : _onBoundingBoxDrawnRef_current.call(onBoundingBoxDrawnRef, rect);
        }
        (_boundingBoxHandler_current = boundingBoxHandler.current) === null || _boundingBoxHandler_current === void 0 ? void 0 : _boundingBoxHandler_current.cleanup();
        boundingBoxHandler.current = null;
        hoveredCornerRef.current = null;
        isHoveringBoxRef.current = false;
        (_onIsSelectingChangeRef_current = onIsSelectingChangeRef.current) === null || _onIsSelectingChangeRef_current === void 0 ? void 0 : _onIsSelectingChangeRef_current.call(onIsSelectingChangeRef, false);
    }, []);
    useEffect(()=>{
        return ()=>{
            var _boundingBoxHandler_current;
            (_boundingBoxHandler_current = boundingBoxHandler.current) === null || _boundingBoxHandler_current === void 0 ? void 0 : _boundingBoxHandler_current.cleanup();
        };
    }, []);
    const editableBoundingBox = editableBoundingBoxIndex !== undefined ? boundingBoxes[editableBoundingBoxIndex] : undefined;
    const onCanvasMouseUp = useCallback((event)=>{
        const boxCanvas = boundingBoxCanvas.current;
        if (!boxCanvas) return;
        const mouseCoords = boxCanvas.getMouseCoords(event);
        if (mouseCoords) {
            var _onMouseUpRef_current;
            const percentageMouseCoords = boxCanvas.convertPointToPercentagePoint(mouseCoords);
            const handled = (_onMouseUpRef_current = onMouseUpRef.current) === null || _onMouseUpRef_current === void 0 ? void 0 : _onMouseUpRef_current.call(onMouseUpRef, {
                point: percentageMouseCoords,
                event,
                isHoveringBox: isHoveringBoxRef.current || !!hoveredCornerRef.current
            });
            if (handled) {
                return;
            }
        }
    }, []);
    // This will clear any existing rects / in context,
    // and set up info to allow for drawing in onMouseMove.
    const onCanvasMouseDown = useCallback((event)=>{
        if (!boundingBoxCanvas.current) return;
        const boxCanvas = boundingBoxCanvas.current;
        if (!boxCanvas) return;
        const mouseCoords = boxCanvas.getMouseCoords(event);
        if (mouseCoords) {
            var _onMouseDownRef_current;
            const percentageMouseCoords = boxCanvas.convertPointToPercentagePoint(mouseCoords);
            const handled = (_onMouseDownRef_current = onMouseDownRef.current) === null || _onMouseDownRef_current === void 0 ? void 0 : _onMouseDownRef_current.call(onMouseDownRef, {
                point: percentageMouseCoords,
                event,
                isHoveringBox: isHoveringBoxRef.current || !!hoveredCornerRef.current
            });
            if (handled) {
                return;
            }
        }
        if (drawNewBox) {
            if (editableBoundingBox) {
                if (isHoveringBoxRef.current) {
                    boundingBoxHandler.current = new BoundingBoxMover({
                        getBoundingBoxCanvas: ()=>boundingBoxCanvas.current,
                        shouldDrawOverlay: true,
                        drawNewBox
                    });
                } else if (hoveredCornerRef.current) {
                    boundingBoxHandler.current = new BoundingBoxResizer({
                        getBoundingBoxCanvas: ()=>boundingBoxCanvas.current,
                        hoveredCorner: hoveredCornerRef.current,
                        shouldDrawOverlay: true,
                        drawNewBox
                    });
                } else {
                    onClear === null || onClear === void 0 ? void 0 : onClear();
                }
            } else {
                onClear === null || onClear === void 0 ? void 0 : onClear();
                boundingBoxHandler.current = new BoundingBoxDrawer({
                    getBoundingBoxCanvas: ()=>boundingBoxCanvas.current,
                    shouldDrawOverlay: false,
                    drawNewBox
                });
            }
        }
        if (boundingBoxHandler.current) {
            var _onIsSelectingChangeRef_current;
            boundingBoxHandler.current.startDrawing({
                event,
                boundingBox: editableBoundingBox,
                onDrawFinished: finishBoxDrawOnMouseUp
            });
            (_onIsSelectingChangeRef_current = onIsSelectingChangeRef.current) === null || _onIsSelectingChangeRef_current === void 0 ? void 0 : _onIsSelectingChangeRef_current.call(onIsSelectingChangeRef, true);
        }
    }, [
        drawNewBox,
        editableBoundingBox,
        onClear,
        finishBoxDrawOnMouseUp
    ]);
    // this callback checks if user hovers over movable corner to set cursor and initialize resizing/moving
    const onMouseMove = useCallback((event)=>{
        if (!!boundingBoxHandler.current) {
            return;
        }
        const boxCanvas = boundingBoxCanvas.current;
        if (!boxCanvas) return;
        const mouseCoords = boxCanvas.getMouseCoords(event);
        if (!mouseCoords) {
            return;
        }
        if (onBoxHovered) {
            const hoveredBox = findHoveredBox({
                boundingBoxes,
                boxCanvas,
                mouseCoords
            });
            onBoxHovered(hoveredBox);
            if (hoveredBox) {
                boxCanvas.setCursor("pointer");
            } else {
                boxCanvas.setDefaultCursor();
            }
        }
        if (editableBoundingBox) {
            const existingRect = boxCanvas.convertPercentageRectToDrawRect(editableBoundingBox);
            const hoveredCorner = isMouseWithinMovableCorner({
                point: mouseCoords,
                rect: existingRect
            });
            hoveredCornerRef.current = hoveredCorner;
            isHoveringBoxRef.current = false;
            if (hoveredCorner) {
                boxCanvas.setResizeCursor(hoveredCorner);
            } else if (isPointInDrawRect(mouseCoords, existingRect)) {
                isHoveringBoxRef.current = true;
                boxCanvas.setCursor("move");
            } else {
                boxCanvas.setDefaultCursor();
            }
        }
    }, [
        boundingBoxes,
        editableBoundingBox,
        onBoxHovered
    ]);
    const redrawTimer = useRef();
    const redrawLayer = useCallback(()=>{
        redrawTimer.current = window.requestAnimationFrame(()=>{
            var _boundingBoxCanvas_current;
            (_boundingBoxCanvas_current = boundingBoxCanvas.current) === null || _boundingBoxCanvas_current === void 0 ? void 0 : _boundingBoxCanvas_current.clearRect();
            if (shouldDrawOverlay) {
                var _boundingBoxCanvas_current1;
                (_boundingBoxCanvas_current1 = boundingBoxCanvas.current) === null || _boundingBoxCanvas_current1 === void 0 ? void 0 : _boundingBoxCanvas_current1.drawOverlay();
            }
            if (boundingBoxCanvas.current) {
                drawBoxes({
                    boxes: boundingBoxes,
                    canvas: boundingBoxCanvas.current
                });
            }
        });
    }, [
        boundingBoxes,
        drawBoxes,
        shouldDrawOverlay
    ]);
    // This effect will clear current boxes, and draw new ones
    useEffect(()=>{
        redrawLayer();
        return ()=>{
            if (redrawTimer.current) {
                window.cancelAnimationFrame(redrawTimer.current);
            }
        };
    }, [
        redrawLayer,
        containerHeight,
        containerWidth
    ]);
    useEffect(()=>{
        if (isUndefined(editableBoundingBoxIndex)) {
            var _boundingBoxCanvas_current;
            (_boundingBoxCanvas_current = boundingBoxCanvas.current) === null || _boundingBoxCanvas_current === void 0 ? void 0 : _boundingBoxCanvas_current.setDefaultCursor();
        }
    }, [
        editableBoundingBoxIndex
    ]);
    if (!containerWidth || !containerHeight) {
        return null;
    }
    const canHandleMouseMove = !disabled && (!!editableBoundingBox || !!onBoxHovered);
    return /*#__PURE__*/ _jsx(DrawingCanvas, {
        hasCustomCursor: hasCustomCursor,
        onMouseMove: canHandleMouseMove ? onMouseMove : undefined,
        onSizeChanged: redrawLayer,
        containerWidth: containerWidth,
        containerHeight: containerHeight,
        onMouseDown: disabled ? undefined : onCanvasMouseDown,
        ref: drawingCanvasRef,
        containerLeft: containerLeft,
        containerTop: containerTop,
        onMouseUp: onCanvasMouseUp
    });
}
export const BoundingBoxLayer = /*#__PURE__*/ memo(_BoundingBoxLayer);
// @ts-ignore - can't get displayName to work with a generic prop - https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
BoundingBoxLayer.displayName = "BoundingBoxLayer";
